import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const ledButtonVariants = cva(
  [
    "relative flex flex-col items-center justify-center gap-1",
    "w-full aspect-square min-h-[72px]",
    "bg-surface-2 border border-border rounded",
    "cursor-pointer transition-[border-color,box-shadow] duration-200",
    "active:translate-y-px",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "disabled:opacity-50 disabled:pointer-events-none",
  ].join(" "),
  {
    variants: {
      color: {
        cyan: "border-led-cyan/25 hover:shadow-[0_0_12px_rgba(110,231,255,0.12)]",
        green: "border-led-green/25 hover:shadow-[0_0_12px_rgba(74,222,128,0.12)]",
        amber: "border-led-amber/25 hover:shadow-[0_0_12px_rgba(232,134,12,0.12)]",
        red: "border-led-red/25 hover:shadow-[0_0_12px_rgba(248,113,113,0.12)]",
        purple: "border-led-purple/25 hover:shadow-[0_0_12px_rgba(167,139,250,0.12)]",
        blue: "border-led-blue/25 hover:shadow-[0_0_12px_rgba(96,165,250,0.12)]",
        empty: "border-dashed border-border/50",
      },
    },
    defaultVariants: {
      color: "empty",
    },
  }
)

const iconColorMap: Record<string, string> = {
  cyan: "text-led-cyan drop-shadow-[0_0_3px_rgba(110,231,255,0.35)]",
  green: "text-led-green drop-shadow-[0_0_3px_rgba(74,222,128,0.35)]",
  amber: "text-led-amber drop-shadow-[0_0_3px_rgba(232,134,12,0.35)]",
  red: "text-led-red drop-shadow-[0_0_3px_rgba(248,113,113,0.35)]",
  purple: "text-led-purple drop-shadow-[0_0_3px_rgba(167,139,250,0.35)]",
  blue: "text-led-blue drop-shadow-[0_0_3px_rgba(96,165,250,0.35)]",
  empty: "text-border",
}

const activeGlowMap: Record<string, string> = {
  cyan: "shadow-[0_0_12px_rgba(110,231,255,0.12)]",
  green: "shadow-[0_0_12px_rgba(74,222,128,0.12)]",
  amber: "shadow-[0_0_12px_rgba(232,134,12,0.12)]",
  red: "shadow-[0_0_12px_rgba(248,113,113,0.12)]",
  purple: "shadow-[0_0_12px_rgba(167,139,250,0.12)]",
  blue: "shadow-[0_0_12px_rgba(96,165,250,0.12)]",
}

const labelColorMap: Record<string, string> = {
  cyan: "text-led-cyan/70",
  green: "text-led-green/70",
  amber: "text-led-amber/70",
  red: "text-led-red/70",
  purple: "text-led-purple/70",
  blue: "text-led-blue/70",
  empty: "text-muted-foreground",
}

export interface LedButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof ledButtonVariants> {
  icon: React.ReactNode
  label: string
  active?: boolean
}

const LedButton = React.forwardRef<HTMLButtonElement, LedButtonProps>(
  ({ className, color, icon, label, active, ...props }, ref) => {
    const colorKey = color ?? "empty"
    return (
      <button
        ref={ref}
        className={cn(
          ledButtonVariants({ color }),
          active && colorKey !== "empty" && activeGlowMap[colorKey],
          className
        )}
        aria-label={label}
        {...props}
      >
        {/* Radial glow backdrop */}
        {colorKey !== "empty" && (
          <span
            className={cn(
              "absolute inset-0 rounded opacity-0 transition-opacity duration-200",
              "group-hover:opacity-100",
              active && "opacity-100"
            )}
            aria-hidden
            style={{
              background: `radial-gradient(ellipse at center, color-mix(in srgb, var(--led-${colorKey}) 6%, transparent) 0%, transparent 70%)`,
            }}
          />
        )}
        <span className={cn("text-[22px] leading-none relative z-10", iconColorMap[colorKey])}>
          {icon}
        </span>
        <span className={cn("font-label text-[9px] uppercase tracking-wide relative z-10", labelColorMap[colorKey])}>
          {label}
        </span>
      </button>
    )
  }
)
LedButton.displayName = "LedButton"

export interface LedButtonGridProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: 3 | 4 | 5 | 6
}

function LedButtonGrid({ className, columns = 4, children, ...props }: LedButtonGridProps) {
  return (
    <div
      className={cn(
        "grid gap-2",
        "grid-cols-3",
        columns === 3 && "md:grid-cols-3",
        columns === 4 && "md:grid-cols-4",
        columns === 5 && "md:grid-cols-5",
        columns === 6 && "md:grid-cols-6",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { LedButtonGrid, LedButton, ledButtonVariants }
